import type { ChatReaction } from "~shared/chatExperienceSurvey";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Auth from "src/Auth";
import type { PersistentChatMessageData } from "~shared/types/persistent-chat-types";
import type { ChatExperienceSurveyPayload } from "./ChatExperienceSurvey";
import { NEW_CHAT_ID } from "./PersistentChatTypes";
import {
  CHAT_EXPERIENCE_SURVEY_INTERVAL,
  countVisibleAssistantReplies,
  isSurveyEligibleByCount,
} from "./chatExperienceSurveyTrigger";

export { CHAT_EXPERIENCE_SURVEY_INTERVAL, countVisibleAssistantReplies, isSurveyEligibleByCount };

const STORAGE_PREFIX = "chatExperienceSurvey:completedReplies:v2:";
const SESSION_DISMISSED_KEY = "chatExperienceSurvey:dismissedThisSession";

function isDismissedThisSession(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(SESSION_DISMISSED_KEY) !== null;
  } catch {
    return false;
  }
}

type SurveySettings = { enabled: boolean; intervalReplies: number };

function storageKey(chatId: string): string {
  return `${STORAGE_PREFIX}${chatId}`;
}

function readCompletedReplyCount(chatId: string): number | null {
  if (!chatId || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey(chatId));
    if (raw == null) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  } catch {
    return null;
  }
}

function writeCompletedReplyCount(chatId: string, count: number): void {
  if (!chatId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(chatId), String(count));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

function clearCompletedReplyCount(chatId: string): void {
  if (!chatId || typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(storageKey(chatId));
  } catch {
    /* ignore privacy-mode errors */
  }
}

/**
 * Drives the "How was your chat experience?" survey. It surfaces after every
 * {@link CHAT_EXPERIENCE_SURVEY_INTERVAL} completed assistant replies and resets
 * its counter each time the user submits or skips.
 */
export function useChatExperienceSurvey({
  chatId,
  projectId,
  messages,
  messagesLoaded,
  streaming,
  pendingAgentResponse,
  hidden,
  mode,
  modelKey,
  newChatMaterializationId,
}: {
  chatId: string | null | undefined;
  projectId: string;
  messages: PersistentChatMessageData[];
  messagesLoaded: boolean;
  streaming: boolean;
  pendingAgentResponse: boolean;
  hidden: boolean;
  mode?: string | null;
  modelKey?: string | null;
  newChatMaterializationId?: string | null;
}) {
  const [dismissedThisSession, setDismissedThisSession] = useState(isDismissedThisSession);
  const assistantReplyCount = useMemo(
    () => countVisibleAssistantReplies(messages),
    [messages],
  );
  const [settings, setSettings] = useState<SurveySettings | null>(null);
  const [settingsRefreshing, setSettingsRefreshing] = useState(true);
  const lastKnownSettingsRef = useRef<SurveySettings | null>(null);
  const settingsRequestIdRef = useRef(0);

  const refreshSettings = useCallback(async () => {
    const requestId = settingsRequestIdRef.current + 1;
    settingsRequestIdRef.current = requestId;
    setSettingsRefreshing(true);
    try {
      const result = await Auth.fetchWithIdToken(
        "/api/ai/agenticChat/experience-survey/config",
        {},
      );
      if (settingsRequestIdRef.current !== requestId) return;
      const enabled = typeof result?.settings?.enabled === "boolean"
        ? result.settings.enabled
        : false;
      const intervalReplies = Number.isInteger(result?.settings?.intervalReplies)
        && result.settings.intervalReplies > 0
        ? result.settings.intervalReplies
        : CHAT_EXPERIENCE_SURVEY_INTERVAL;
      const nextSettings = { enabled, intervalReplies };
      lastKnownSettingsRef.current = nextSettings;
      setSettings(nextSettings);
      setSettingsRefreshing(false);
    } catch (error) {
      Sentry.captureException(error);
      if (settingsRequestIdRef.current === requestId) {
        setSettings(lastKnownSettingsRef.current ?? {
          enabled: false,
          intervalReplies: CHAT_EXPERIENCE_SURVEY_INTERVAL,
        });
        setSettingsRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    void refreshSettings();
    return () => { settingsRequestIdRef.current += 1; };
  }, [chatId, refreshSettings]);

  const responseBusy = streaming || pendingAgentResponse;
  const latestVisibleAssistantMessageId = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message?.role === "assistant" && !message.metadata?.hiddenFromUI) return message.id;
    }
    return null;
  }, [messages]);
  const wasResponseBusyRef = useRef(responseBusy);
  const lastCountedAssistantMessageIdRef = useRef<string | null>(null);
  const [replyProgress, setReplyProgress] = useState<{
    chatId: string | null;
    count: number | null;
  }>({ chatId: null, count: null });

  useEffect(() => {
    if (!chatId) {
      setReplyProgress({ chatId: null, count: null });
      wasResponseBusyRef.current = responseBusy;
      lastCountedAssistantMessageIdRef.current = null;
      return;
    }
    if (!messagesLoaded) return;
    if (replyProgress.chatId === chatId && replyProgress.count != null) return;

    const isNewChatMaterialization = replyProgress.chatId === NEW_CHAT_ID
      && chatId !== NEW_CHAT_ID
      && newChatMaterializationId === chatId;
    const storedCount = chatId === NEW_CHAT_ID ? null : readCompletedReplyCount(chatId);
    const count = isNewChatMaterialization
      ? replyProgress.count ?? storedCount ?? 0
      : storedCount ?? 0;
    if (storedCount == null || isNewChatMaterialization) {
      writeCompletedReplyCount(chatId, count);
    }
    if (isNewChatMaterialization) {
      clearCompletedReplyCount(NEW_CHAT_ID);
    }
    if (!isNewChatMaterialization) {
      wasResponseBusyRef.current = responseBusy;
      lastCountedAssistantMessageIdRef.current = latestVisibleAssistantMessageId;
    }
    setReplyProgress({ chatId, count });
  }, [
    chatId,
    messagesLoaded,
    newChatMaterializationId,
    latestVisibleAssistantMessageId,
    replyProgress.chatId,
    replyProgress.count,
    responseBusy,
  ]);

  useEffect(() => {
    if (!chatId || replyProgress.chatId !== chatId || replyProgress.count == null) {
      const isNewChatMaterialization = replyProgress.chatId === NEW_CHAT_ID
        && chatId !== NEW_CHAT_ID
        && newChatMaterializationId === chatId;
      if (!isNewChatMaterialization) wasResponseBusyRef.current = responseBusy;
      return;
    }

    const completedResponse = wasResponseBusyRef.current && !responseBusy;
    wasResponseBusyRef.current = responseBusy;
    if (
      !completedResponse
      || !latestVisibleAssistantMessageId
      || latestVisibleAssistantMessageId === lastCountedAssistantMessageIdRef.current
    ) return;

    lastCountedAssistantMessageIdRef.current = latestVisibleAssistantMessageId;
    void refreshSettings();
    setReplyProgress((current) => {
      if (current.chatId !== chatId || current.count == null) return current;
      const nextCount = current.count + 1;
      writeCompletedReplyCount(chatId, nextCount);
      return { chatId, count: nextCount };
    });
  }, [chatId, latestVisibleAssistantMessageId, newChatMaterializationId, refreshSettings, replyProgress.chatId, replyProgress.count, responseBusy]);

  const completedRepliesSincePrompt = replyProgress.chatId === chatId ? replyProgress.count : null;
  const eligibleByCount = completedRepliesSincePrompt != null
    && settings != null
    && isSurveyEligibleByCount(completedRepliesSincePrompt, 0, settings.intervalReplies);

  const mountSurvey = Boolean(
    chatId
    && chatId !== NEW_CHAT_ID
    && projectId
    && settings?.enabled
    && !dismissedThisSession
    && !isDismissedThisSession()
    && !hidden
    && eligibleByCount
  );
  const awaitingCompletionSettings = Boolean(
    wasResponseBusyRef.current
    && !responseBusy
    && latestVisibleAssistantMessageId
    && latestVisibleAssistantMessageId !== lastCountedAssistantMessageIdRef.current
  );
  const showSurvey = Boolean(
    mountSurvey
    && !settingsRefreshing
    && !awaitingCompletionSettings
    && !streaming
    && !pendingAgentResponse
  );

  const resetReplyProgress = useCallback(() => {
    if (!chatId) return;
    writeCompletedReplyCount(chatId, 0);
    setReplyProgress({ chatId, count: 0 });
  }, [chatId]);

  const submitSurvey = useCallback(
    async (payload: { reaction: ChatReaction | null; skipped: boolean; feedbackText?: string; dontAskAgain?: boolean }) => {
      if (!chatId || chatId === NEW_CHAT_ID || !projectId) return false;
      // Session opt-out must not wait for (or depend on) the survey API.
      if (payload.dontAskAgain) {
        setDismissedThisSession(true);
        try {
          window.sessionStorage.setItem(SESSION_DISMISSED_KEY, "true");
        } catch {
          // Keep suppression in memory when browser storage is unavailable.
        }
      }
      try {
        await Auth.fetchWithIdToken("/api/ai/agenticChat/experience-survey", {
          chatId,
          projectId,
          reaction: payload.reaction,
          messageId: latestVisibleAssistantMessageId,
          skipped: payload.skipped,
          feedbackText: payload.feedbackText,
          assistantReplyCount,
          mode: mode ?? undefined,
          modelKey: modelKey ?? undefined,
          intervalReplies: settings?.intervalReplies ?? CHAT_EXPERIENCE_SURVEY_INTERVAL,
        });
        resetReplyProgress();
        return true;
      } catch (error) {
        Sentry.captureException(error);
        return false;
      }
    },
    [chatId, projectId, resetReplyProgress, assistantReplyCount, mode, modelKey, settings, latestVisibleAssistantMessageId],
  );

  const handleSubmit = useCallback(
    (payload: ChatExperienceSurveyPayload) => submitSurvey({
      reaction: payload.reaction,
      skipped: false,
      feedbackText: payload.feedbackText,
      dontAskAgain: payload.dontAskAgain,
    }),
    [submitSurvey],
  );

  const handleSkip = useCallback(
    (dontAskAgain = false) => submitSurvey({ reaction: null, skipped: true, dontAskAgain }),
    [submitSurvey],
  );

  return { mountSurvey, showSurvey, handleSubmit, handleSkip };
}
